import type { ReactNode } from 'react';

import IconLink from 'components/IconLink';
import Status from 'components/Status';
import Text from 'components/Text';
import type { OpeningHours as OpeningHoursType } from 'models/openingHours';
import type { HeadingLevel } from 'models/sitecore';
import { cn } from 'utils/classNames';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';
import { getHeadingLevelBelow } from 'utils/string';

interface OpeningHoursProps {
	className?: string;
	openingHours: OpeningHoursType[];
}

function OpeningHours({ className, openingHours }: OpeningHoursProps) {
	if (openingHours.length === 0) {
		return null;
	}

	return (
		<ul className={cn('space-y-2 font-body text-md', className)}>
			{openingHours.map(
				({ dateStr, shortDescription, title, type, weekday }) => (
					<li
						key={`${weekday}-${title}-${dateStr}`}
						className="flex justify-between gap-6"
					>
						{type?.toUpperCase() === 'SPECIAL'
							? `${title} (${dateStr})`
							: weekday}
						: <span>{shortDescription}</span>
					</li>
				),
			)}
		</ul>
	);
}
OpeningHours.displayName = 'StoreInfo_OpeningHours';

interface Props {
	addressSectionClassName?: string;
	afterAddressContent?: ReactNode;
	city: string;
	facebookLink: string | undefined;
	facebookLinkClassName?: string;
	/**
	 * Heading level for the store name. Must be provided even if no name is set
	 * since it's used to determine heading levels for opening hours. If the name
	 * is not set, match the level with the closest heading before this component.
	 */
	headingHierarchyStartLevel: Exclude<HeadingLevel, 'h5' | 'h6'>;
	name?: string;
	postalCode: string;
	regularOpeningHours: OpeningHoursType[] | undefined;
	socialMediaHeadingClassName?: string;
	specialOpeningHours: OpeningHoursType[] | undefined;
	storeArea: string;
	streetAddress: string;
	todaysOpeningHours: OpeningHoursType | undefined;
}

export default function StoreInfo({
	addressSectionClassName = 'max-md:flex max-md:flex-wrap max-md:justify-between',
	afterAddressContent,
	city,
	facebookLink,
	facebookLinkClassName = 'mt-4',
	headingHierarchyStartLevel,
	name,
	postalCode,
	regularOpeningHours,
	socialMediaHeadingClassName = 'mt-r-3xl',
	specialOpeningHours,
	storeArea,
	streetAddress,
	todaysOpeningHours,
}: Props) {
	const { t } = useI18n();

	const hasSpecialOpeningHours = is.arrayWithLength(specialOpeningHours);
	const hasRegularOpeningHours = is.arrayWithLength(regularOpeningHours);
	return (
		<>
			{name && (
				<Text as={headingHierarchyStartLevel} text={name} className="mb-6" />
			)}

			{todaysOpeningHours?.description && (
				<Status
					variant={
						todaysOpeningHours.state?.toUpperCase() === 'OPEN'
							? 'positive'
							: 'negative'
					}
					text={todaysOpeningHours.description}
					className="mb-4"
				/>
			)}

			<div className={addressSectionClassName}>
				<Text as="p">
					{storeArea}
					{storeArea && <br />}
					{streetAddress}
					{streetAddress && <br />}
					{postalCode} {city}
				</Text>
			</div>

			{afterAddressContent}

			{(hasRegularOpeningHours || hasSpecialOpeningHours) && (
				<>
					<Text
						as={getHeadingLevelBelow(headingHierarchyStartLevel)}
						text={t('stores_opening_hours_heading')}
						className={cn('mb-r-sm', !afterAddressContent && 'mt-r-lg')}
					/>
					{hasRegularOpeningHours && (
						<OpeningHours openingHours={regularOpeningHours} />
					)}
					{hasSpecialOpeningHours && (
						<>
							<Text
								as={getHeadingLevelBelow(headingHierarchyStartLevel, 2)}
								styleAs="p"
								text={t('stores_special_opening_hours_heading')}
								className="mt-8 mb-r-sm font-bold"
							/>
							<OpeningHours openingHours={specialOpeningHours} />
						</>
					)}
				</>
			)}
			{facebookLink && (
				<>
					<Text
						as={getHeadingLevelBelow(headingHierarchyStartLevel)}
						text={t('stores_social_media_heading')}
						className={socialMediaHeadingClassName}
					/>
					<IconLink
						className={facebookLinkClassName}
						text={t('stores_facebook_link_text')}
						href={facebookLink}
						target="_blank"
						icon="facebook-background"
						size="md"
					/>
				</>
			)}
		</>
	);
}
StoreInfo.displayName = 'StoreInfo';
